/**
 * Customer reviews carousel — prev/next scroll + edge opacity by visible columns.
 */
class CustomerReviewsCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {number | null} */
  #raf = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-reviews-track]');
    this.#prev = this.querySelector('[data-reviews-prev]');
    this.#next = this.querySelector('[data-reviews-next]');
    this.#cards = Array.from(this.querySelectorAll('[data-reviews-card]'));

    if (!this.#track || this.#cards.length === 0) return;

    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });

    this.#update();
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);

    if (this.#raf != null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
  }

  #onPrev = () => {
    this.#scrollBy(-1);
  };

  #onNext = () => {
    this.#scrollBy(1);
  };

  #onScroll = () => {
    if (this.#raf != null) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#update();
    });
  };

  #getColumns() {
    const value = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--customer-reviews-columns')
    );
    return Number.isFinite(value) && value > 0 ? Math.round(value) : 1;
  }

  #cardStep() {
    const card = this.#cards[0];
    if (!card) return 0;

    const trackList = this.querySelector('.customer-reviews__track');
    const styles = trackList ? getComputedStyle(trackList) : null;
    const gap = styles ? Number.parseFloat(styles.columnGap || styles.gap) || 16 : 16;
    return card.getBoundingClientRect().width + gap;
  }

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    const track = this.#track;
    if (!track) return;

    const step = this.#cardStep();
    if (step <= 0) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (maxScroll <= 0) return;

    let nextLeft = track.scrollLeft + direction * step;

    if (nextLeft < 0) {
      nextLeft = maxScroll;
    } else if (nextLeft > maxScroll + 1) {
      nextLeft = 0;
    }

    track.scrollTo({ left: nextLeft, behavior: 'smooth' });
  }

  #update() {
    const track = this.#track;
    if (!track) return;

    const columns = this.#getColumns();
    const step = this.#cardStep();
    if (step <= 0) return;

    const startIndex = Math.round(track.scrollLeft / step);

    this.#cards.forEach((card, index) => {
      const slot = index - startIndex;
      let isEdge = false;

      if (slot < 0 || slot >= columns) {
        // Outside the visible window
        isEdge = true;
      } else if (columns >= 3) {
        // Outer visible cards faded; middle active (e.g. 4-up: 1st & 4th faded)
        isEdge = slot === 0 || slot === columns - 1;
      } else {
        // 1–2 columns: all visible cards stay active
        isEdge = false;
      }

      card.classList.toggle('is-edge', isEdge);
    });
  }
}

if (!customElements.get('customer-reviews-carousel')) {
  customElements.define('customer-reviews-carousel', CustomerReviewsCarousel);
}
